import { isSameDay, startOfDay } from 'date-fns';
import { useCallback, useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useShowPlus15PaymentTerms } from '~/services/company-settings';
import { DocumentBuilderForm } from '~/types/document-builder';
import { FlowPaymentTerms } from '~/types/flow';
import { calculatePresetDate } from '~/utils/flow';

export const usePresetDates = () => {
  const { t } = useTranslation('documents', { keyPrefix: 'builder' });
  const baseDate = useWatch<DocumentBuilderForm, 'date'>({ name: 'date' });
  const plus15Enabled = useShowPlus15PaymentTerms();

  return useMemo(
    () => [
      {
        id: 'EOM',
        date: calculatePresetDate(baseDate, FlowPaymentTerms.EOM),
        label: t('fields.flow_date.delay_eom'),
        term: FlowPaymentTerms.EOM,
      },
      {
        id: '15',
        date: calculatePresetDate(baseDate, FlowPaymentTerms.Plus15),
        label: t('fields.flow_date.delay_15'),
        term: FlowPaymentTerms.Plus15,
      },
      {
        id: '30',
        date: calculatePresetDate(baseDate, FlowPaymentTerms.Plus30),
        label: t('fields.flow_date.delay_30'),
        term: FlowPaymentTerms.Plus30,
      },
      {
        id: '60',
        date: calculatePresetDate(baseDate, FlowPaymentTerms.Plus60),
        label: t('fields.flow_date.delay_60'),
        term: FlowPaymentTerms.Plus60,
      },
      {
        id: '90',
        date: calculatePresetDate(baseDate, FlowPaymentTerms.Plus90),
        label: t('fields.flow_date.delay_90'),
        term: FlowPaymentTerms.Plus90,
      },
      {
        id: '120',
        date: calculatePresetDate(baseDate, FlowPaymentTerms.Plus120),
        label: t('fields.flow_date.delay_120'),
        term: FlowPaymentTerms.Plus120,
      },
      {
        id: '150',
        date: calculatePresetDate(baseDate, FlowPaymentTerms.Plus150),
        label: t('fields.flow_date.delay_150'),
        term: FlowPaymentTerms.Plus150,
      },
      {
        id: '30EOM',
        date: calculatePresetDate(baseDate, FlowPaymentTerms.Plus30EOM),
        label: t('fields.flow_date.delay_30eom'),
        term: FlowPaymentTerms.Plus30EOM,
      },
      ...(plus15Enabled
        ? [
            {
              id: 'EOMPLUS15',
              date: calculatePresetDate(baseDate, FlowPaymentTerms.EOMPlus15),
              label: t('fields.flow_date.delay_eomplus15'),
              term: FlowPaymentTerms.EOMPlus15,
            },
          ]
        : []),
      ...(plus15Enabled
        ? [
            {
              id: '30EOMPLUS15',
              date: calculatePresetDate(baseDate, FlowPaymentTerms.Plus30EOMPlus15),
              label: t('fields.flow_date.delay_30eomplus15'),
              term: FlowPaymentTerms.Plus30EOMPlus15,
            },
          ]
        : []),
      {
        id: '60EOM',
        date: calculatePresetDate(baseDate, FlowPaymentTerms.Plus60EOM),
        label: t('fields.flow_date.delay_60eom'),
        term: FlowPaymentTerms.Plus60EOM,
      },
      ...(plus15Enabled
        ? [
            {
              id: '60EOMPLUS15',
              date: calculatePresetDate(baseDate, FlowPaymentTerms.Plus60EOMPlus15),
              label: t('fields.flow_date.delay_60eomplus15'),
              term: FlowPaymentTerms.Plus60EOMPlus15,
            },
          ]
        : []),
      {
        id: '90EOM',
        date: calculatePresetDate(baseDate, FlowPaymentTerms.Plus90EOM),
        label: t('fields.flow_date.delay_90eom'),
        term: FlowPaymentTerms.Plus90EOM,
      },
      ...(plus15Enabled
        ? [
            {
              id: '90EOMPLUS15',
              date: calculatePresetDate(baseDate, FlowPaymentTerms.Plus90EOMPlus15),
              label: t('fields.flow_date.delay_90eomplus15'),
              term: FlowPaymentTerms.Plus90EOMPlus15,
            },
          ]
        : []),
      {
        id: '120EOM',
        date: calculatePresetDate(baseDate, FlowPaymentTerms.Plus120EOM),
        label: t('fields.flow_date.delay_120eom'),
        term: FlowPaymentTerms.Plus120EOM,
      },
      ...(plus15Enabled
        ? [
            {
              id: '120EOMPLUS15',
              date: calculatePresetDate(baseDate, FlowPaymentTerms.Plus120EOMPlus15),
              label: t('fields.flow_date.delay_120eomplus15'),
              term: FlowPaymentTerms.Plus120EOMPlus15,
            },
          ]
        : []),
      {
        id: '150EOM',
        date: calculatePresetDate(baseDate, FlowPaymentTerms.Plus150EOM),
        label: t('fields.flow_date.delay_150eom'),
        term: FlowPaymentTerms.Plus150EOM,
      },
    ],
    [baseDate, t, plus15Enabled],
  );
};

export const useUpdateDate = () => {
  const { getValues, setValue } = useFormContext<DocumentBuilderForm>();
  const presetDates = usePresetDates();

  return useCallback(
    (date: Date) => {
      setValue('date', date);
      const flows = getValues('flows');
      const counterpartPaymentTerm = getValues('counterpart')?.paymentDate;
      flows.forEach((flow, index) => {
        const flowTerm = presetDates.find((p) => isSameDay(p.date, flow.expectedPaymentDate))?.term;
        if (flowTerm) {
          setValue(`flows.${index}.expectedPaymentDate`, calculatePresetDate(date, flowTerm));
        } else if (counterpartPaymentTerm === null) {
          // Documents with instant payments do not have a payment term set
          setValue(`flows.${index}.expectedPaymentDate`, startOfDay(date));
        }
      });
    },
    [getValues, presetDates, setValue],
  );
};
